import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, ExternalLink, RefreshCw, Maximize2, TrendingUp, TrendingDown, Minus, Brain, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradingChartModal = ({ isOpen, onClose, symbol, interval = 'D' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [key, setKey] = useState(0);
  const [showPatternAnalysis, setShowPatternAnalysis] = useState(false);
  const [patternAnalysis, setPatternAnalysis] = useState(null);
  const [analyzingPatterns, setAnalyzingPatterns] = useState(false);

  // Reset state when symbol changes
  useEffect(() => {
    if (isOpen && symbol) {
      setIsLoading(true);
      setKey(prev => prev + 1);
      setPatternAnalysis(null);
      setShowPatternAnalysis(false);
    }
  }, [isOpen, symbol]);

  // Analyze candlestick patterns
  const analyzeCandlestickPatterns = async () => {
    if (!symbol) return;
    
    setAnalyzingPatterns(true);
    setShowPatternAnalysis(true);
    
    try {
      const response = await axios.post(`${API}/analyze/candlestick-patterns`, {
        symbol: symbol.replace(/.*:/, ''), // Remove exchange prefix if present
        period: '3mo'
      });
      
      if (response.data.success) {
        setPatternAnalysis(response.data);
        toast.success(`Pattern analysis complete for ${symbol}`);
      }
    } catch (error) {
      console.error('Error analyzing patterns:', error);
      toast.error('Failed to analyze candlestick patterns');
      setPatternAnalysis(null);
    } finally {
      setAnalyzingPatterns(false);
    }
  };

  const openFullTradingView = () => {
    // Don't prefix with exchange - let TradingView auto-resolve
    const tvSymbol = symbol?.includes(':') ? symbol : symbol;
    window.open(`https://www.tradingview.com/chart/?symbol=${tvSymbol}`, '_blank');
  };

  // Pop out chart to a separate window for side-by-side work
  const popOutChart = () => {
    // Don't prefix with exchange - let TradingView auto-resolve
    const tvSymbol = symbol?.includes(':') ? symbol : symbol;
    const widgetUrl = `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_popup&symbol=${encodeURIComponent(tvSymbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=1&locale=en`;
    
    // Calculate window position for side-by-side
    const width = 800;
    const height = 600;
    const left = window.screen.width - width - 50;
    const top = 100;
    
    window.open(
      widgetUrl,
      `chart_${symbol}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
    
    // Close the modal after popping out
    onClose();
  };

  const refreshChart = () => {
    setIsLoading(true);
    setKey(prev => prev + 1);
  };

  // Build TradingView widget URL
  const getWidgetUrl = () => {
    if (!symbol) return '';
    // Don't prefix with exchange - let TradingView auto-resolve the correct exchange
    const tvSymbol = symbol.includes(':') ? symbol : symbol;
    // Using TradingView's embed widget URL
    return `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=${encodeURIComponent(tvSymbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&locale=en&utm_source=localhost&utm_medium=widget_new&utm_campaign=chart&utm_term=${encodeURIComponent(tvSymbol)}`;
  };

  if (!isOpen) return null;

  // Get direction icon and color
  const getDirectionDisplay = () => {
    if (!patternAnalysis) return null;
    const dir = patternAnalysis.direction;
    if (dir === 'BULLISH') return { icon: <TrendingUp className="h-5 w-5" />, color: 'bg-green-500', text: 'BULLISH 📈' };
    if (dir === 'BEARISH') return { icon: <TrendingDown className="h-5 w-5" />, color: 'bg-red-500', text: 'BEARISH 📉' };
    return { icon: <Minus className="h-5 w-5" />, color: 'bg-gray-500', text: 'NEUTRAL ⚖️' };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[85vh] p-0 overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b bg-gradient-to-r from-slate-50 to-blue-50">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3">
              <DialogTitle className="text-xl font-bold">{symbol} - Live Chart</DialogTitle>
              
              {/* Pattern Analysis Button - TOP RIGHT */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={analyzeCandlestickPatterns}
                disabled={analyzingPatterns}
                className={`text-xs ${showPatternAnalysis && patternAnalysis ? 'bg-purple-100 border-purple-300' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0'}`}
                title="AI Candlestick Pattern Analysis"
                data-testid="pattern-analysis-btn"
              >
                {analyzingPatterns ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Brain className="h-3 w-3 mr-1" />
                )}
                {analyzingPatterns ? 'Analyzing...' : '🕯️ Pattern Analysis'}
              </Button>
              
              {/* Direction Badge when analysis is done */}
              {patternAnalysis && (
                <Badge className={`${getDirectionDisplay()?.color} text-white text-sm px-3 py-1`}>
                  {getDirectionDisplay()?.icon}
                  <span className="ml-1">{getDirectionDisplay()?.text}</span>
                </Badge>
              )}
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={popOutChart}
                className="text-xs bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                title="Open in separate window for side-by-side view"
              >
                <Maximize2 className="h-3 w-3 mr-1" />
                Pop Out Window
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openFullTradingView}
                className="text-xs"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open in TradingView
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshChart}
                className="text-xs"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex" style={{ height: 'calc(85vh - 60px)' }}>
          {/* Chart Area */}
          <div className={`relative ${showPatternAnalysis && patternAnalysis ? 'w-2/3' : 'w-full'} transition-all duration-300`}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
                  <p className="text-gray-600">Loading {symbol} chart...</p>
                </div>
              </div>
            )}
            <iframe
              key={key}
              src={getWidgetUrl()}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
              onLoad={() => setIsLoading(false)}
              title={`${symbol} TradingView Chart`}
            />
          </div>
          
          {/* Pattern Analysis Panel */}
          {showPatternAnalysis && (
            <div className="w-1/3 border-l bg-gradient-to-b from-slate-50 to-white overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center">
                    🕯️ Candlestick Analysis
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowPatternAnalysis(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {analyzingPatterns ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
                    <p className="text-gray-600">Analyzing candlestick patterns...</p>
                    <p className="text-sm text-gray-400 mt-2">Using AI to detect patterns</p>
                  </div>
                ) : patternAnalysis ? (
                  <div className="space-y-4">
                    {/* Direction Badge */}
                    <div className={`p-4 rounded-lg text-center ${
                      patternAnalysis.direction === 'BULLISH' 
                        ? 'bg-green-100 border-2 border-green-300' 
                        : patternAnalysis.direction === 'BEARISH'
                          ? 'bg-red-100 border-2 border-red-300'
                          : 'bg-gray-100 border-2 border-gray-300'
                    }`}>
                      <div className="text-3xl mb-2">
                        {patternAnalysis.direction === 'BULLISH' ? '📈' : patternAnalysis.direction === 'BEARISH' ? '📉' : '⚖️'}
                      </div>
                      <div className={`text-xl font-bold ${
                        patternAnalysis.direction === 'BULLISH' 
                          ? 'text-green-700' 
                          : patternAnalysis.direction === 'BEARISH'
                            ? 'text-red-700'
                            : 'text-gray-700'
                      }`}>
                        {patternAnalysis.direction}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Current: ${patternAnalysis.current_price} | Trend: {patternAnalysis.trend}
                      </div>
                    </div>
                    
                    {/* Analysis Text */}
                    <div className="bg-white rounded-lg border p-4">
                      <h4 className="font-semibold text-purple-700 mb-2 flex items-center">
                        <Brain className="h-4 w-4 mr-2" />
                        AI Pattern Analysis
                      </h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {patternAnalysis.analysis}
                      </div>
                    </div>
                    
                    {/* Recent Candles Summary */}
                    {patternAnalysis.candle_data && (
                      <div className="bg-white rounded-lg border p-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Recent Candles</h4>
                        <div className="space-y-1 text-xs">
                          {patternAnalysis.candle_data.slice(-5).map((candle, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center justify-between p-2 rounded ${
                                candle.bullish ? 'bg-green-50' : 'bg-red-50'
                              }`}
                            >
                              <span className="font-medium">{candle.date}</span>
                              <span>{candle.bullish ? '🟢' : '🔴'}</span>
                              <span>O:{candle.open} H:{candle.high}</span>
                              <span>L:{candle.low} C:{candle.close}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Re-analyze button */}
                    <Button 
                      onClick={analyzeCandlestickPatterns}
                      className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Re-analyze Patterns
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Click "Pattern Analysis" to analyze candlestick patterns</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to open chart directly in new window (for use without modal)
export const openChartInNewWindow = (symbol, interval = 'D') => {
  if (!symbol) return;
  
  const tvSymbol = symbol.includes(':') ? symbol : `NASDAQ:${symbol}`;
  const widgetUrl = `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_popup&symbol=${encodeURIComponent(tvSymbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=1&locale=en`;
  
  const width = 800;
  const height = 600;
  const left = window.screen.width - width - 50;
  const top = 100;
  
  window.open(
    widgetUrl,
    `chart_${symbol}`,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
  );
};

// Simple embed component using iframe
export const TradingChartEmbed = ({ symbol, height = '500px', interval = 'D' }) => {
  const [isLoading, setIsLoading] = useState(true);

  if (!symbol) return null;

  const tvSymbol = symbol.includes(':') ? symbol : `NASDAQ:${symbol}`;
  const widgetUrl = `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_embed&symbol=${encodeURIComponent(tvSymbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1`;

  return (
    <div style={{ height, width: '100%', position: 'relative' }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <RefreshCw className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      )}
      <iframe
        src={widgetUrl}
        style={{ width: '100%', height: '100%', border: 'none' }}
        allowFullScreen
        onLoad={() => setIsLoading(false)}
        title={`${symbol} Chart`}
      />
    </div>
  );
};

// Mini chart using TradingView mini widget
export const MiniChart = ({ symbol, height = '200px' }) => {
  if (!symbol) return null;

  const tvSymbol = symbol.includes(':') ? symbol : `NASDAQ:${symbol}`;
  const widgetUrl = `https://s.tradingview.com/embed-widget/mini-symbol-overview/?symbol=${encodeURIComponent(tvSymbol)}&locale=en&dateRange=1M&colorTheme=light&isTransparent=false&autosize=true`;

  return (
    <div style={{ height, width: '100%' }}>
      <iframe
        src={widgetUrl}
        style={{ width: '100%', height: '100%', border: 'none' }}
        allowFullScreen
        title={`${symbol} Mini Chart`}
      />
    </div>
  );
};

export default TradingChartModal;
